import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { CheckCircle, TrendingUp, Target, Shield, Eye } from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';
import { t } from '@/i18n/translations';

const whyIcons = [Eye, TrendingUp, Shield, Target];

const SeiUnProprietario = () => {
  const { lang } = useLanguage();
  const p = t('proprietario', lang);
  const fadeIn = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.6 } };

  const renderBold = (text) => {
    if (!text) return null;
    const parts = text.split(/\*\*(.*?)\*\*/g);
    return parts.map((part, i) =>
      i % 2 === 1 ? <strong key={`bold-${part.slice(0, 15)}`} className="text-[#65c3ba]">{part}</strong> : part
    );
  };

  return (
    <div className="bg-white pt-32 pb-16" data-testid="sei-un-proprietario-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div {...fadeIn} className="text-center mb-12">
          <h1 className="text-3xl sm:text-4xl font-bold text-[#0A1312] mb-6" style={{fontFamily: "'Cabinet Grotesk', sans-serif"}}>{p.title}</h1>
        </motion.div>

        <motion.div {...fadeIn} className="max-w-4xl mx-auto mb-16">
          <p className="text-lg text-[#0A1312] mb-6 leading-relaxed" style={{fontFamily: "'Satoshi', sans-serif"}}>
            {renderBold(p.intro1)}
          </p>
          <p className="text-lg text-[#0A1312] leading-relaxed" style={{fontFamily: "'Satoshi', sans-serif"}}>
            {renderBold(p.intro2)}
          </p>
        </motion.div>
      </div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }} className="relative py-16 md:py-24 mb-16 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1765277676100-7a252c868fb6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwzfHxMdWdhbm8lMjBTd2l0emVybGFuZCUyMGFlcmlhbCUyMGNpdHlzY2FwZSUyMGxha2V8ZW58MHx8fHwxNzc2MDI5ODk2fDA&ixlib=rb-4.1.0&q=85')", backgroundAttachment: 'fixed' }} />
        <div className="absolute inset-0 bg-[#3e716c]/80" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-white text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-4" style={{fontFamily: "'Cabinet Grotesk', sans-serif"}}>{p.roleTitle}</h2>
          <p className="text-lg mb-6" style={{fontFamily: "'Satoshi', sans-serif"}}>{p.roleText}</p>
          <div className="max-w-2xl mx-auto space-y-3">
            <p className="flex items-center justify-center text-lg" style={{fontFamily: "'Satoshi', sans-serif"}}>
              <CheckCircle className="mr-3 flex-shrink-0" size={22} />{p.roleItem1}
            </p>
            <p className="flex items-center justify-center text-lg" style={{fontFamily: "'Satoshi', sans-serif"}}>
              <CheckCircle className="mr-3 flex-shrink-0" size={22} />{p.roleItem2}
            </p>
          </div>
        </div>
      </motion.div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Why it pays - flowing text, no heavy borders */}
        <motion.div {...fadeIn} transition={{ delay: 0.2 }} className="max-w-4xl mx-auto mb-16">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#0A1312] mb-8 text-center" style={{fontFamily: "'Cabinet Grotesk', sans-serif"}}>{p.whyTitle}</h2>
          <p className="text-lg text-[#0A1312] mb-6 leading-relaxed" style={{fontFamily: "'Satoshi', sans-serif"}}>{p.whyText1}</p>
          <p className="text-lg text-[#0A1312] mb-6 leading-relaxed" style={{fontFamily: "'Satoshi', sans-serif"}}>{p.whyText2}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
            {p.whyItems.map((text, idx) => {
              const Icon = whyIcons[idx];
              return (
                <div key={`why-${text.slice(0, 20)}`} className="flex items-center py-3">
                  <Icon className="text-[#65c3ba] mr-3 flex-shrink-0" size={22} />
                  <span className="text-[#0A1312] font-medium" style={{fontFamily: "'Satoshi', sans-serif"}}>{text}</span>
                </div>
              );
            })}
          </div>
          <p className="text-lg text-[#0A1312] mb-6 leading-relaxed" style={{fontFamily: "'Satoshi', sans-serif"}}>{p.whyText3}</p>
          <p className="text-xl font-semibold text-[#65c3ba] text-center mt-8" style={{fontFamily: "'Cabinet Grotesk', sans-serif"}}>{p.whyHighlight}</p>
        </motion.div>

        {/* Practical Example */}
        <motion.div {...fadeIn} transition={{ delay: 0.3 }} className="mb-16">
          <h2 className="text-3xl font-bold text-[#0A1312] mb-8 text-center" style={{fontFamily: "'Cabinet Grotesk', sans-serif"}}>{p.exampleTitle}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-[#F7F9F9] p-8 rounded-sm">
              <h3 className="text-xl font-bold text-[#0A1312] mb-4" style={{fontFamily: "'Cabinet Grotesk', sans-serif"}}>{p.tradTitle}</h3>
              <div className="space-y-3 text-[#0A1312]" style={{fontFamily: "'Satoshi', sans-serif"}}>
                <div className="flex justify-between"><span>{p.tradMonthly}</span><span className="font-semibold whitespace-nowrap ml-4">CHF 1.500</span></div>
                <div className="flex justify-between"><span>{p.tradYearly}</span><span className="font-semibold whitespace-nowrap ml-4">CHF 18.000</span></div>
                <div className="border-t border-[#E2E8F0] pt-3 flex justify-between text-[#0A1312] font-bold"><span>{p.tradProfit}</span><span className="whitespace-nowrap ml-4">CHF 18.000</span></div>
              </div>
            </div>
            <div className="bg-[#65c3ba]/10 p-8 border-2 border-[#65c3ba] rounded-sm">
              <h3 className="text-xl font-bold text-[#0A1312] mb-4" style={{fontFamily: "'Cabinet Grotesk', sans-serif"}}>{p.hwTitle}</h3>
              <div className="space-y-3 text-[#0A1312]" style={{fontFamily: "'Satoshi', sans-serif"}}>
                <div className="flex justify-between"><span>{p.hwRevenue}</span><span className="font-semibold whitespace-nowrap ml-4">CHF 40.000</span></div>
                <div className="flex justify-between"><span>{p.hwCosts}</span><span className="font-semibold whitespace-nowrap ml-4">-CHF 17.500</span></div>
                <div className="border-t border-[#65c3ba] pt-3 flex justify-between text-[#0A1312] font-bold text-lg"><span>{p.hwProfit}</span><span className="text-[#65c3ba] whitespace-nowrap ml-4">~CHF 22.500</span></div>
              </div>
              <div className="mt-4 p-3 bg-[#3e716c] text-white rounded-sm text-center font-bold">{p.hwBadge}</div>
            </div>
          </div>
          <p className="text-center text-sm text-[#0A1312] mt-6" style={{fontFamily: "'Satoshi', sans-serif"}}>{p.exampleNote}</p>
        </motion.div>
      </div>

      <motion.div {...fadeIn} transition={{ delay: 0.4 }} className="relative overflow-hidden text-center" data-testid="sei-un-proprietario-cta">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1772475329856-7c7de908b44e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwyfHx3YXJtJTIwbW9kZXJuJTIwYXBhcnRtZW50JTIwaW50ZXJpb3IlMjBjb3p5JTIwbGl2aW5nJTIwcm9vbXxlbnwwfHx8fDE3NzYwMzA1NTZ8MA&ixlib=rb-4.1.0&q=85')", backgroundAttachment: 'fixed' }} />
        <div className="absolute inset-0 bg-[#3e716c]/80" />
        <div className="relative z-10 py-16 px-4 text-white">
          <h2 className="text-2xl font-bold mb-4" style={{fontFamily: "'Cabinet Grotesk', sans-serif"}}>{p.ctaTitle}</h2>
          <p className="text-white/70 mb-8" style={{fontFamily: "'Satoshi', sans-serif"}}>{p.ctaText}</p>
          <Link to="/valutazione-gratuita">
            <Button className="bg-[#65c3ba] text-white hover:bg-white hover:text-[#3e716c] rounded-sm px-8 py-6 text-lg">{p.ctaBtn}</Button>
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

export default SeiUnProprietario;